/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import type {
  InputDescription,
  IntegrationDescription,
} from "@smartsuite/automation-engine-api";
import {
  DropdownPanel,
  KeyValueCustomSelect,
  MenuOption,
  MenuOptionWrapper,
  PickerInputField,
  useSelectState,
} from "@smartsuite/react-ui/lib";

import { observer } from "mobx-react-lite";
import type { Ref } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CaptionBlock } from "../../../../../../../../components/CaptionBlock";
import { useTheme } from "../../../../../../../../stores";
import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../../../../../types/candidate";
import type { DynamicSection } from "../../../../../../../../types/line";
import { getLocalCssColorVariables } from "../../../../../../../../utils/cssVariablesUtils";
import {
  convertActionsToDynamicSections,
  convertTriggerToDynamicSection,
} from "../../../../utils";
import { StyledContainerWrapper } from "../../../style";
import { ActionOptions } from "../../containers/ActionOptions";
import { TriggerOptions } from "../../containers/TriggerOptions";
import {
  actionsToOptions,
  calculateConfigValue,
  ifLoopingSourceIsActionFind,
} from "../utils";
import type { NumberPill, Option } from "./utils";
import {
  useConverters,
  ejectPillFromInput,
  mergeNextPillToInput,
  triggerToOptions,
  getIsMenuAvaliable,
} from "./utils";
import { ValueDescription_T } from "@smartsuite/automation-engine-api";
import { POSITION_AT, POSITION_MY } from "../../../../style";
import { useRootStore } from "../../../../../../../../stores/hooks";
import {
  opensTriggerFieldsDirectly,
  resolveCurrentItemScope,
} from "../../../../utils/action-sources";
import { FieldMissingError } from "../../../../../../Sections/ErrorSection";
import { useFieldMissingValidation } from "../../../../../../../../stores/automation/hooks/field-missing-validation";
import { ActionLabel } from "../../../../../../../../utils/constants";
import { UpdateValueDropdown } from "../../containers/UpdateValueDropdown";
import { isMathOption } from "../../containers/UpdateValueDropdown/utils";
import type { UpdateValueType } from "../../containers/UpdateValueDropdown/components/UpdateValueOption";
import type { MathOptions } from "../../containers/UpdateValueDropdown/components/MathOption";
import { isTruthy } from "remeda";
import type { PillNodeAttrs } from "@smartsuite/smartdoc/lib/esm/extensions/pill/extension";
import * as S from "./style";
import { SolutionColorVariables } from "@smartsuite/types";
import { CurrentOption } from "../../containers/CurrentOption";
import { Tab } from "../../containers/TriggerDropdown/TriggerDropdown";
import { useLoopingMenu } from "../../../../hooks/use-looping-menu";
import { createLoopItemPill } from "../../../../utils/createLoopItemPill";

export const DurationEditor = observer<{
  config: InputCandidate;
  trigger: TriggerCandidate;
  actions: ActionCandidate[];
  action?: ActionCandidate;
  onChange: (input: InputCandidate) => void;
  integrations: IntegrationDescription[];
  onClickTrash?: () => void;
  isError?: boolean;
  description?: InputDescription;
  maxValue?: number;
}>(
  ({
    config,
    trigger,
    actions,
    action,
    onChange,
    integrations,
    description,
    isError,
    onClickTrash,
    maxValue,
  }) => {
    const { t } = useTranslation();
    const store = useRootStore();
    const theme = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const [tab, setTab] = useState<
      "root" | "trigger-fields" | "action-fields" | "options" | "current"
    >("root");
    const [triggerFields, setTriggerFields] = useState<Option[]>([]);
    const [actionsFields, setActionsFields] = useState<Option[]>([]);
    const [triggerMenu, setTriggerMenu] = useState<DynamicSection | null>(null);
    const [actionMenus, setActionMenus] = useState<DynamicSection[]>([]);
    const [selectedActionMenu, setSelectedActionMenu] = useState<
      DynamicSection | undefined
    >();
    const [focus, setFocus] = useSelectState();
    const buttonRef = useRef<HTMLButtonElement>();
    const [pills, setPills] = useState<NumberPill[]>([]);
    const [showMeasure, setShowMeasure] = useState<boolean>(true);
    const [hasSingleMenuItem, setHasSingleMenuItem] = useState<boolean>(false);
    const { isInputValidByFieldMissing, getFieldMissingErrorMessage } =
      useFieldMissingValidation();
    const editorWrapperId = `${config.inputId}-editor-wrapper`;
    const [actionType, setActionType] = useState<
      | "replace"
      | "clear"
      | "append"
      | "mathAdd"
      | "mathSubtract"
      | "mathMultiply"
      | "mathDivide"
      | undefined
    >(config.operatorConfig?.operator?.oneofKind);
    const [isCleanAction, setIsCleanAction] = useState<boolean>(false);
    const rootLooping = store.automations.candidate.looping;
    const looping = action
      ? (rootLooping?.scopeForAction?.(action.hash) ?? rootLooping)
      : rootLooping;
    const actionGroupType =
      store.automations.candidate.currentActionGroup?.config.oneofKind ??
      "actions";

    const { convertInputToPills } = useConverters();

    const { loopingMenu, loopingFields } = useLoopingMenu({
      trigger,
      actions,
      integrations,
      types: [ValueDescription_T.NUMBER],
      ssFieldTypes: [
        "numberfield",
        "currencyfield",
        "percentfield",
        "percentcompletefield",
        "numbersliderfield",
        "ratingfield",
        "countfield",
        "rollupfield",
        "votefield",
        "durationfield",
      ],
      systemAllowedOptions: [],
      action,
    });

    const offersCurrentItem =
      resolveCurrentItemScope(store.automations.candidate, undefined) !==
      undefined;

    useEffect(() => {
      if (
        opensTriggerFieldsDirectly(actionMenus, triggerMenu, offersCurrentItem)
      ) {
        setTab("trigger-fields");
        setHasSingleMenuItem(true);
      } else {
        setTab("root");
        setHasSingleMenuItem(false);
      }
    }, [actionMenus, triggerMenu, offersCurrentItem]);

    useEffect(() => {
      setTriggerMenu(
        convertTriggerToDynamicSection(
          trigger,
          integrations,
          [ValueDescription_T.NUMBER],
          isMathOption(actionType as UpdateValueType | MathOptions)
            ? [ValueDescription_T.NUMBER]
            : [ValueDescription_T.NUMBER, ValueDescription_T.STRING],
          [
            "numberfield",
            "currencyfield",
            "percentfield",
            "percentcompletefield",
            "numbersliderfield",
            "ratingfield",
            "countfield",
            "rollupfield",
            "votefield",
            "textfield",
            "textareafield",
            "durationfield",
          ],
          [""],
        ),
      );
      setTriggerFields(triggerToOptions(trigger));
    }, [integrations, trigger, actionType]);

    useEffect(() => {
      if (action !== undefined) {
        setActionsFields(
          actionsToOptions(
            actions,
            [ValueDescription_T.NUMBER, ValueDescription_T.STRING],
            [
              "numberfield",
              "currencyfield",
              "percentfield",
              "percentcompletefield",
              "numbersliderfield",
              "ratingfield",
              "countfield",
              "rollupfield",
              "votefield",
              "durationfield",
              "textfield",
            ],
          ),
        );
        setActionMenus(
          convertActionsToDynamicSections(
            actions,
            action,
            integrations,
            [ValueDescription_T.NUMBER, ValueDescription_T.STRING],
            [
              "numberfield",
              "currencyfield",
              "percentfield",
              "percentcompletefield",
              "numbersliderfield",
              "ratingfield",
              "countfield",
              "rollupfield",
              "votefield",
              "durationfield",
              "textfield",
            ],
            [],
            looping,
          ),
        );
      }
    }, [actions, action, integrations]);

    useEffect(() => {
      const pills = convertInputToPills(
        config,
        triggerFields,
        actionsFields,
        trigger,
        actions,
        action,
        loopingFields,
      );

      setPills(pills);
    }, [actionsFields, config, triggerFields]);

    useEffect(() => {
      if (isCleanAction) {
        setShowMeasure(false);
      } else {
        setShowMeasure(true);
      }
    }, [isCleanAction]);

    const addPill = (attrs: NumberPill | PillNodeAttrs): void => {
      setTimeout(() => {
        setIsOpen(false);
      });

      const newPills = [attrs];
      onChange({
        ...config,
        value: calculateConfigValue(newPills, actions, trigger),
      });
    };

    useEffect(() => {
      if (store.inputToFocus?.inputId === config.inputId) {
        setIsOpen(!!isMenuAvaliable);
        const focusTimeoutId = setTimeout(() => {
          const wrapper = document.getElementById(editorWrapperId);
          const editor = wrapper?.querySelector(".picker-input-field__input");
          // @ts-expect-error
          editor?.focus();
          store.inputToFocus = undefined;
        });
        return () => clearTimeout(focusTimeoutId);
      }
    }, []);

    useEffect(() => {
      if (actionType === undefined) return;

      if (actionType === "clear") {
        setIsOpen(false);
        setIsCleanAction(true);
      } else {
        setIsCleanAction(false);
      }

      if (isMathOption(actionType as UpdateValueType | MathOptions)) {
        const newPills = pills.filter((pill) => {
          if (pill.type === "triggerOutput") {
            const isOptionExist = triggerMenu?.options.find(
              (option) => option.outputId === pill.value,
            );
            return isTruthy(isOptionExist);
          }
          return true;
        });
        if (newPills.length !== pills.length) {
          onChange({
            ...config,
            value: calculateConfigValue(newPills, actions, trigger),
            operatorConfig: {
              // @ts-expect-error
              operator: {
                oneofKind: actionType,
                [actionType]: {},
              },
            },
          });
          return;
        }
      }

      onChange({
        ...config,
        value:
          actionType === "clear"
            ? {
                values: [],
              }
            : config.value,
        operatorConfig: {
          // @ts-expect-error
          operator: {
            oneofKind: actionType,
            [actionType]: {},
          },
        },
      });
    }, [actionType, triggerMenu]);

    const isNextPillAllowed = (config: InputCandidate): boolean => {
      if (config.value?.values.length === 1) {
        if (focus) {
          setFocus(false);
        }
        return false;
      } else {
        return true;
      }
    };

    const changeActionType = (
      type:
        | "replace"
        | "clear"
        | "append"
        | "mathAdd"
        | "mathSubtract"
        | "mathMultiply"
        | "mathDivide"
        | undefined,
    ): void => {
      setActionType(type);
    };

    const triggerOutputs = triggerMenu?.options ?? [];
    const isMenuAvaliable = getIsMenuAvaliable(
      triggerOutputs,
      actionMenus,
      actionGroupType,
      looping.isConfiguredSourceValid,
    );

    const disabledOptionId = pills.map((pill) => pill.title);

    return (
      <StyledContainerWrapper>
        {description !== undefined && (
          <CaptionBlock description={description} onClickTrash={onClickTrash} />
        )}
        <S.Root>
          <S.ReactInputFieldContainer id={editorWrapperId}>
            <S.Row>
              <S.Input showMeasure={showMeasure}>
                <PickerInputField
                  showAddButton={!isCleanAction}
                  className={`${
                    (isError ?? !isInputValidByFieldMissing(config))
                      ? "is-error"
                      : ""
                  } ${isCleanAction ? "is-clear" : ""}`}
                  state={
                    (isError ?? !isInputValidByFieldMissing(config))
                      ? "error"
                      : isOpen || focus
                        ? "active"
                        : "default"
                  }
                  onChangeFocused={(isFocused, event) => {
                    setFocus(isFocused);

                    if (
                      isFocused &&
                      isMenuAvaliable &&
                      event?.target.className !== "field-type-pill__remove"
                    ) {
                      setIsOpen(true);
                    }
                  }}
                  onInputChange={() => {
                    setIsOpen(false);
                  }}
                  pickerIcon={isMenuAvaliable ? undefined : ""}
                  // @ts-expect-error
                  buttonRef={buttonRef as Ref<HTMLButtonElement>}
                  isOpen={isOpen}
                  pills={pills}
                  pillStyle={{
                    textSize: "m",
                  }}
                  onPillRemove={(index: number) => {
                    setIsOpen(false);
                    onChange(ejectPillFromInput(config, index));
                  }}
                  allowManualInput={!isCleanAction && isNextPillAllowed(config)}
                  onInputAdd={(number: string) => {
                    setIsOpen(false);
                    if (maxValue !== undefined && Number(number) > maxValue) {
                      return;
                    }
                    onChange(
                      mergeNextPillToInput(config, {
                        type: "custom",
                        number,
                      }),
                    );
                  }}
                  placeholder={
                    isCleanAction
                      ? t("automation-form.clean-operator-editor-placeholder")
                      : focus
                        ? t("automation-form.number-editor-placeholder")
                        : ""
                  }
                  pattern={/^[0-9]*([0-9]+[,.][0-9]*)?$/}
                >
                  {action?.actionReference?.actionId ===
                    ActionLabel.updateRecord &&
                    description?.operators?.operators !== undefined &&
                    description?.operators?.operators.length > 0 && (
                      <UpdateValueDropdown
                        operatorConfig={config.operatorConfig}
                        operatorDescriptions={description?.operators.operators}
                        onOpenedChange={setFocus}
                        onChange={changeActionType}
                      />
                    )}
                  {isOpen && (
                    <DropdownPanel
                      targetElementRef={buttonRef}
                      isActive={isOpen}
                      at={POSITION_AT}
                      my={POSITION_MY}
                      minWidth={300}
                      arrowBackground="custom"
                      arrowCustomBackground={SolutionColorVariables.sc1}
                      panelLineBackground={SolutionColorVariables.sc1}
                      portalStyles={getLocalCssColorVariables(theme)}
                      onClickOutside={() => setIsOpen(false)}
                      onCloseAnimationEnd={() => setIsOpen(false)}
                    >
                      {tab === "root" && (
                        <MenuOptionWrapper>
                          <MenuOption
                            submenuArrow="right"
                            onClick={() => setTab("trigger-fields")}
                          >
                            {t("field-popups.trigger-popup")}
                          </MenuOption>
                          {actionMenus.map(
                            (actionMenu) =>
                              actionMenu?.options.length > 0 && (
                                <MenuOption
                                  key={actionMenu.hash}
                                  submenuArrow="right"
                                  onClick={() => {
                                    setSelectedActionMenu(actionMenu);
                                    setTab("action-fields");
                                  }}
                                >
                                  {actionMenu.hideNamePrefix === true
                                    ? actionMenu.name
                                    : t("field-popups.action-popup", {
                                        name: actionMenu.name,
                                      })}
                                </MenuOption>
                              ),
                          )}
                          <CurrentOption
                            loopingMenu={loopingMenu}
                            addPill={() => {
                              // Case when source for looping is the find action
                              if (
                                ifLoopingSourceIsActionFind(looping) ||
                                loopingMenu?.options?.length
                              ) {
                                setTab("current");
                                return;
                              }
                              const loopPill = createLoopItemPill(looping);
                              if (loopPill) {
                                addPill(loopPill);
                              }
                            }}
                          />
                        </MenuOptionWrapper>
                      )}

                      {tab === "trigger-fields" && (
                        <TriggerOptions
                          searchPlaceholder={t("automation-form.search-fields")}
                          title={t("automation-form.fields-from-trigger")}
                          onNavigateBack={() =>
                            !hasSingleMenuItem && setTab("root")
                          }
                          addPill={addPill}
                          options={triggerOutputs}
                          disabledOptionId={disabledOptionId}
                          hasSingleMenuItem={hasSingleMenuItem}
                        />
                      )}
                      {tab === "current" && loopingMenu && (
                        <TriggerOptions
                          tab={Tab.LOOPING_FIELDS}
                          searchPlaceholder={t("automation-form.search-fields")}
                          title={loopingMenu.name}
                          onNavigateBack={() => setTab("root")}
                          addPill={addPill}
                          options={loopingMenu.options}
                          hasSingleMenuItem={false}
                          loopingMenuHash={loopingMenu.hash}
                          disabledOptionId={disabledOptionId}
                          loopingMenu={loopingMenu}
                        />
                      )}
                      {tab === "action-fields" && (
                        <ActionOptions
                          onNavigateBack={() => setTab("root")}
                          addPill={addPill}
                          selectedActionMenu={selectedActionMenu}
                          selectedActionHash={selectedActionMenu?.hash}
                        />
                      )}
                    </DropdownPanel>
                  )}
                </PickerInputField>
              </S.Input>
              {showMeasure && (
                <S.Measure>
                  <KeyValueCustomSelect
                    my="left top+2"
                    disabled={true}
                    value="hours"
                    showClear={false}
                    placeholderArrowStyle="filled-dark"
                    options={[{ label: "Hours", value: "hours" }]}
                    portalStyles={getLocalCssColorVariables(theme)}
                  />
                </S.Measure>
              )}
            </S.Row>

            <FieldMissingError
              message={getFieldMissingErrorMessage(config, action)}
            />
          </S.ReactInputFieldContainer>
        </S.Root>
      </StyledContainerWrapper>
    );
  },
);
